import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { loanAPI } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from '../../utils/i18n';
import { formatCurrency } from '../../utils/currency';
import { FileText, User, Building, Calendar, DollarSign, AlertCircle, Download, PlusCircle } from 'lucide-react';
import { useDialog } from '../../components/Dialog';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import PageHeader from './components/PageHeader';
import LoanIncreaseRequestList from '../../components/LoanIncreaseRequestList';
import './LoanDetailPage.css';

const LoanDetailPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { language } = useAuth();
  const { t } = useTranslation(language);
  const { showError, showSuccess, DialogComponent } = useDialog();
  const [loan, setLoan] = useState(null);
  const [paymentSchedule, setPaymentSchedule] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLoanDetail();
  }, [id]);

  const fetchLoanDetail = async () => {
    try {
      const [loanRes, scheduleRes] = await Promise.all([
        loanAPI.getOne(id),
        loanAPI.getPaymentSchedule(id).catch(() => ({ data: [] })),
      ]);

      // API returns { loan: {...} } so we need to unwrap it
      const loanData = loanRes.data?.loan || loanRes.loan || loanRes.data;
      setLoan(loanData);

      // Ensure payment schedule is an array and sort by remaining balance (descending)
      const scheduleData = scheduleRes.data?.schedule || scheduleRes.schedule || scheduleRes.data || [];
      const sortedSchedule = Array.isArray(scheduleData)
        ? scheduleData.sort((a, b) => (b.remainingPrincipal || 0) - (a.remainingPrincipal || 0))
        : [];
      setPaymentSchedule(sortedSchedule);
    } catch (error) {
      console.error('Failed to fetch loan detail:', error);
      showError(t('cannotLoadCustomer'));
      setTimeout(() => navigate('/loan/list'), 1500);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString(language === 'vi' ? 'vi-VN' : language === 'ko' ? 'ko-KR' : 'en-US');
  };

  const exportToPDF = () => {
    const doc = new jsPDF();

    // Add title
    doc.setFontSize(16);
    doc.text(t('paymentSchedule'), 14, 15);

    // Add loan info
    doc.setFontSize(10);
    doc.text(`${t('customerName')}: ${loan.customerName}`, 14, 25);
    doc.text(`${t('loanAmount')}: ${formatCurrency(loan.amount)}`, 14, 32);
    doc.text(`${t('loanTerm')}: ${loan.termMonths} ${t('months')}`, 14, 39);
    doc.text(`${t('disbursedDate')}: ${formatDate(loan.disbursedAt)}`, 14, 46);

    // Prepare table data
    const tableData = paymentSchedule.map((payment) => [
      formatDate(payment.dueDate),
      formatCurrency(payment.interest),
      formatCurrency(payment.principal),
      formatCurrency(payment.totalAmount || (payment.principal + payment.interest)),
      formatCurrency(payment.remainingPrincipal || 0),
    ]);

    // Add table using autoTable
    autoTable(doc, {
      startY: 52,
      head: [[
        t('paymentPeriod'),
        t('expectedInterest'),
        t('principalPayment'),
        t('totalPayment'),
        t('remainingBalance'),
      ]],
      body: tableData,
      styles: { fontSize: 8, font: 'helvetica' },
      headStyles: { fillColor: [102, 126, 234], textColor: 255 },
      alternateRowStyles: { fillColor: [245, 245, 245] },
    });

    // Save PDF
    doc.save(`payment-schedule-${loan.code || loan.id}.pdf`);
  };

  const exportToExcel = () => {
    // Prepare data for Excel
    const excelData = [
      [t('paymentSchedule')],
      [],
      [t('customerName'), loan.customerName],
      [t('loanAmount'), formatCurrency(loan.amount)],
      [t('loanTerm'), `${loan.termMonths} ${t('months')}`],
      [t('disbursedDate'), formatDate(loan.disbursedAt)],
      [],
      [
        t('paymentPeriod'),
        t('expectedInterest'),
        t('principalPayment'),
        t('totalPayment'),
        t('remainingBalance'),
      ],
      ...paymentSchedule.map((payment) => [
        formatDate(payment.dueDate),
        payment.interest,
        payment.principal,
        payment.totalAmount || (payment.principal + payment.interest),
        payment.remainingPrincipal || 0,
      ]),
    ];

    // Create worksheet
    const ws = XLSX.utils.aoa_to_sheet(excelData);

    // Set column widths
    ws['!cols'] = [
      { wch: 20 },
      { wch: 20 },
      { wch: 20 },
      { wch: 20 },
      { wch: 20 },
    ];

    // Create workbook
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Payment Schedule');

    // Save Excel file
    XLSX.writeFile(wb, `payment-schedule-${loan.code || loan.id}.xlsx`);
  };

  const getStatusInfo = (status) => {
    const statusMap = {
      pending: { label: t('pending'), color: 'warning', icon: '⏳' },
      approved: { label: t('approved'), color: 'info', icon: '✓' },
      rejected: { label: t('rejected'), color: 'danger', icon: '✕' },
      disbursed: { label: t('disbursed'), color: 'success', icon: '💰' },
      repaying: { label: t('repaying'), color: 'primary', icon: '📊' },
      completed: { label: t('completed'), color: 'success', icon: '✓' },
    };
    return statusMap[status] || statusMap.pending;
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
        <p>{t('loading')}</p>
      </div>
    );
  }

  if (!loan) {
    return (
      <div className="empty-state">
        <p>{t('notFound')}</p>
        <button className="btn btn-primary" onClick={() => navigate('/loan/list')}>
          {t('backToList')}
        </button>
      </div>
    );
  }

  const statusInfo = getStatusInfo(loan.status);

  return (
    <div className="loan-detail-page">
      <DialogComponent />
      <PageHeader title={t('loanDetail')} backTo="/loan/list" />

      <div className="page-content">
        {/* Status Banner */}
        <div className={`status-banner ${statusInfo.color}`}>
          <span className="status-icon">{statusInfo.icon}</span>
          <div className="status-info">
            <div className="status-label">{t('statusLabel')}</div>
            <div className="status-value">{statusInfo.label}</div>
          </div>
          <div className="loan-code-display">{loan.code || `${t('loanCode')} #${loan.id}`}</div>
        </div>

        {/* Increase Amount Button - Only for pending/approved loans */}
        {(loan.status === 'pending' || loan.status === 'approved') && (
          <div className="action-buttons" style={{ marginTop: '1rem', marginBottom: '1rem' }}>
            <button
              className="btn btn-warning"
              onClick={() => navigate('/loan/increase-request', { state: { loan } })}
              style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
            >
              <PlusCircle size={18} />
              {t('requestLoanChange')}
            </button>
          </div>
        )}

        {/* Rejection Reason */}
        {loan.status === 'rejected' && loan.rejectionReason && (
          <div className="alert alert-danger">
            <AlertCircle size={20} />
            <div>
              <strong>{t('rejectionReason')}:</strong>
              <p>{loan.rejectionReason}</p>
            </div>
          </div>
        )}

        {/* Customer Info */}
        <div className="detail-section">
          <h2 className="section-title">
            <User size={20} />
            {t('customerInfoSection')}
          </h2>
          <div className="detail-grid">
            <div className="detail-item">
              <span className="detail-label">{t('fullName')}:</span>
              <span className="detail-value">{loan.customerName}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">{t('phone')}:</span>
              <span className="detail-value">{loan.customerPhone}</span>
            </div>
            {loan.customerEmail && (
              <div className="detail-item">
                <span className="detail-label">{t('email')}:</span>
                <span className="detail-value">{loan.customerEmail}</span>
              </div>
            )}
            <div className="detail-item">
              <span className="detail-label">{t('idNumber')}:</span>
              <span className="detail-value">{loan.customerIdNumber}</span>
            </div>
            {loan.customerAddress && (
              <div className="detail-item full-width">
                <span className="detail-label">{t('address')}:</span>
                <span className="detail-value">{loan.customerAddress}</span>
              </div>
            )}
          </div>
        </div>

        {/* Bank Info */}
        <div className="detail-section">
          <h2 className="section-title">
            <Building size={20} />
            {t('bankInfoSection')}
          </h2>
          <div className="detail-grid">
            <div className="detail-item">
              <span className="detail-label">{t('bank')}:</span>
              <span className="detail-value">
                {loan.bank?.name || loan.bankName || 'N/A'}
              </span>
            </div>
            {(loan.branch?.name || loan.branchName) && (
              <div className="detail-item">
                <span className="detail-label">{t('branch')}:</span>
                <span className="detail-value">
                  {loan.branch?.name || loan.branchName}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Loan Info */}
        <div className="detail-section">
          <h2 className="section-title">
            <DollarSign size={20} />
            {t('loanInfoSection')}
          </h2>
          <div className="detail-grid">
            <div className="detail-item highlight">
              <span className="detail-label">{t('loanAmount')}:</span>
              <span className="detail-value large">{formatCurrency(loan.amount)}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">{t('loanTerm')}:</span>
              <span className="detail-value">{loan.termMonths} {t('months')}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">{t('createdDate')}:</span>
              <span className="detail-value">{formatDate(loan.createdAt)}</span>
            </div>
            {loan.disbursedAt && (
              <div className="detail-item">
                <span className="detail-label">{t('disbursedDate')}:</span>
                <span className="detail-value">{formatDate(loan.disbursedAt)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Notes */}
        {loan.notes && (
          <div className="detail-section">
            <h2 className="section-title">
              <FileText size={20} />
              {t('notes')}
            </h2>
            <div className="notes-box">{loan.notes}</div>
          </div>
        )}

        {/* Payment Schedule */}
        {loan.status === 'disbursed' || loan.status === 'repaying' || loan.status === 'completed' ? (
          <div className="detail-section">
            <div className="section-header-with-actions">
              <h2 className="section-title">
                <Calendar size={20} />
                {t('paymentSchedule')}
              </h2>
              {paymentSchedule.length > 0 && (
                <div className="export-buttons">
                  <button className="btn-export btn-excel" onClick={exportToExcel}>
                    <Download size={16} />
                    Excel
                  </button>
                  <button className="btn-export btn-pdf" onClick={exportToPDF}>
                    <Download size={16} />
                    PDF
                  </button>
                </div>
              )}
            </div>

            {paymentSchedule.length === 0 ? (
              <div className="empty-state-small">
                {t('scheduleAfterDisburse')}
              </div>
            ) : (
              <div className="payment-schedule">
                <div className="schedule-table-wrapper">
                  <table className="payment-schedule-table">
                    <thead>
                      <tr>
                        <th>{t('paymentPeriod')}</th>
                        <th>{t('expectedInterest')}</th>
                        <th>{t('principalPayment')}</th>
                        <th>{t('totalPayment')}</th>
                        <th>{t('remainingBalance')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paymentSchedule.map((payment, index) => (
                        <tr key={index} className={payment.isPaid ? 'paid' : ''}>
                          <td className="period-cell">{formatDate(payment.dueDate)}</td>
                          <td className="number-cell">{formatCurrency(payment.interest)}</td>
                          <td className="number-cell">{formatCurrency(payment.principal)}</td>
                          <td className="number-cell total-cell">
                            {formatCurrency(payment.totalAmount || (payment.principal + payment.interest))}
                          </td>
                          <td className="number-cell remaining-cell">
                            {formatCurrency(payment.remainingPrincipal || 0)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        ) : null}

        {/* Increase Request History */}
        <LoanIncreaseRequestList loanId={loan.id} />
      </div>
    </div>
  );
};

export default LoanDetailPage;
